import { useEffect, useState } from 'react'
import { getNfcReaderPort, nfcServers } from '@/lib/nfc'

// Observe reader status only; discovery must never dispatch card scans.
export default function useNfcDiscovery(enabled) {
    const [servers, setServers] = useState({})

    useEffect(() => {
        setServers({})
        if (!enabled) return
        let disposed = false
        const cleanups = nfcServers.map((server) => {
            let socket
            let retryTimer
            let statusTimer
            let urlIndex = 0

            function update(state, ports = []) {
                if (disposed) return
                setServers((current) => ({
                    ...current,
                    [server.id]: { state, ports },
                }))
            }

            function retry() {
                if (disposed) return
                update('unavailable')
                urlIndex = (urlIndex + 1) % server.urls.length
                retryTimer = setTimeout(connect, 5000)
            }

            function connect() {
                if (disposed) return
                let ws
                try {
                    ws = new WebSocket(server.urls[urlIndex])
                } catch {
                    retry()
                    return
                }
                socket = ws
                // A successful handshake without reader status is not discovery.
                statusTimer = setTimeout(() => {
                    update('unavailable')
                    ws.close()
                }, 10000)
                ws.onmessage = (event) => {
                    if (disposed) return
                    let data
                    try {
                        data = JSON.parse(event.data)
                    } catch {
                        return
                    }
                    if (!data || data.type !== 'reader_status') return
                    if (data.server?.id ? data.server.id !== server.id : server.id !== 'printserver') {
                        ws.close()
                        return
                    }
                    if (!Array.isArray(data.readers)) return
                    clearTimeout(statusTimer)
                    update('connected', data.readers
                        .map((reader) => getNfcReaderPort(reader, server.id))
                        .filter(Boolean))
                }
                ws.onerror = () => ws.close()
                ws.onclose = () => {
                    clearTimeout(statusTimer)
                    retry()
                }
            }

            connect()
            return () => {
                clearTimeout(retryTimer)
                clearTimeout(statusTimer)
                socket?.close()
            }
        })
        return () => {
            disposed = true
            cleanups.forEach((cleanup) => cleanup())
        }
    }, [enabled])

    return enabled ? servers : {}
}
